import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Phone, Mail, MessageCircle, Clock, Send, CheckCircle2 } from "lucide-react";

const PHONE_DISPLAY = "+44 1234 567 890";
const PHONE_LINK = "+441234567890";
const EMAIL = "info@luxorservicesconcierge.com";

const HERO_IMG =
  "https://images.unsplash.com/photo-1607082349566-187342175e2f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete required fields");
      return;
    }
    setSending(true);
    try {
      // Reuse partner-applications style storage — send to a lightweight generic endpoint if you add one.
      // For now, we treat this as a booking with service_slug=concierge just to persist. Alternatively skip API.
      await api.post("/partner-applications", {
        full_name: form.name,
        email: form.email,
        phone: form.phone || "0000000000",
        sector: "other",
        sector_other: `Contact form: ${form.subject || "General enquiry"}`,
        availability: "both",
        contact_preference: "either",
        message: form.message,
      });
      setDone(true);
      toast.success("Message received. We'll be in touch.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page" className="bg-[#F5EFE4]">
      {/* Hero */}
      <section className="relative pt-32 pb-16 bg-[#1A1815] overflow-hidden">
        <div className="absolute inset-0 opacity-25" style={{ backgroundImage: `url('${HERO_IMG}')`, backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1A1815] via-[#1A1815]/85 to-[#1A1815]/40" />
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10">
          <p className="overline">Get in Touch</p>
          <h1 className="font-serif text-5xl lg:text-7xl mt-4 leading-[1.02] text-[#F5EFE4] max-w-3xl">
            Say hello to <em className="italic text-[#C5A059]">Luxor.</em>
          </h1>
          <div className="mt-4"><span className="divider-gold-center" /></div>
          <p className="mt-6 max-w-xl text-[#F5EFE4]/70">
            Reach us by phone, email, or the concierge chat. Whatever the request — however small — we are listening.
          </p>
        </div>
      </section>

      {/* Contact channels */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="contact-channels">
          {/* Phone */}
          <a
            href={`tel:${PHONE_LINK}`}
            className="group bg-white border border-[#E4D9C1] p-8 hover:border-[#C5A059] transition-colors flex flex-col"
            data-testid="contact-phone"
          >
            <div className="w-12 h-12 rounded-full bg-[#F5EFE4] border border-[#E4D9C1] flex items-center justify-center text-[#C5A059] group-hover:bg-[#C5A059] group-hover:text-white group-hover:border-[#C5A059] transition-colors">
              <Phone size={20} strokeWidth={1.5} />
            </div>
            <p className="overline mt-6">Phone</p>
            <h3 className="font-serif text-3xl mt-2 text-[#1A1815]">{PHONE_DISPLAY}</h3>
            <p className="text-sm text-[#6B665E] mt-3 flex items-center gap-2">
              <Clock size={13} className="text-[#C5A059]" /> 9am – 9pm · Monday to Sunday
            </p>
            <span className="mt-5 text-xs uppercase tracking-[0.24em] text-[#C5A059] inline-flex items-center gap-1.5">
              Tap to call →
            </span>
          </a>

          {/* Email */}
          <a
            href={`mailto:${EMAIL}`}
            className="group bg-white border border-[#E4D9C1] p-8 hover:border-[#C5A059] transition-colors flex flex-col"
            data-testid="contact-email"
          >
            <div className="w-12 h-12 rounded-full bg-[#F5EFE4] border border-[#E4D9C1] flex items-center justify-center text-[#C5A059] group-hover:bg-[#C5A059] group-hover:text-white group-hover:border-[#C5A059] transition-colors">
              <Mail size={20} strokeWidth={1.5} />
            </div>
            <p className="overline mt-6">Email</p>
            <h3 className="font-serif text-2xl mt-2 text-[#1A1815] break-all">{EMAIL}</h3>
            <p className="text-sm text-[#6B665E] mt-3">We reply within 4 hours during working hours.</p>
            <span className="mt-5 text-xs uppercase tracking-[0.24em] text-[#C5A059] inline-flex items-center gap-1.5">
              Compose email →
            </span>
          </a>

          {/* Live chat */}
          <Link
            to="/concierge"
            className="group bg-white border border-[#E4D9C1] p-8 hover:border-[#C5A059] transition-colors flex flex-col"
            data-testid="contact-chat"
          >
            <div className="w-12 h-12 rounded-full bg-[#F5EFE4] border border-[#E4D9C1] flex items-center justify-center text-[#C5A059] group-hover:bg-[#C5A059] group-hover:text-white group-hover:border-[#C5A059] transition-colors">
              <MessageCircle size={20} strokeWidth={1.5} />
            </div>
            <p className="overline mt-6">Live Chat</p>
            <h3 className="font-serif text-2xl mt-2 text-[#1A1815]">AI Concierge · 24/7</h3>
            <p className="text-sm text-[#6B665E] mt-3">
              Instant answers on services, pricing and bookings — any hour.
            </p>
            <span className="mt-5 text-xs uppercase tracking-[0.24em] text-[#C5A059] inline-flex items-center gap-1.5">
              Start chat →
            </span>
          </Link>
        </div>
      </section>

      {/* Message form */}
      <section className="max-w-[1100px] mx-auto px-6 lg:px-10 pb-24">
        <div className="text-center">
          <p className="overline">Send a Message</p>
          <h2 className="font-serif text-4xl lg:text-5xl mt-4 text-[#1A1815]">Or write to us here.</h2>
          <div className="mt-4 flex justify-center"><span className="divider-gold-center" /></div>
          <p className="mt-4 text-[#6B665E]">A concierge will read this personally and respond within a few hours.</p>
        </div>

        {done ? (
          <div className="mt-12 bg-white border border-[#C5A059] p-12 text-center max-w-2xl mx-auto" data-testid="contact-success">
            <div className="w-14 h-14 rounded-full bg-[#C5A059] text-white flex items-center justify-center mx-auto"><CheckCircle2 size={26} /></div>
            <p className="overline mt-6">Received</p>
            <h3 className="font-serif text-3xl mt-3 text-[#1A1815]">Thank you.</h3>
            <p className="mt-4 text-[#6B665E]">We'll respond shortly at {form.email}.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-12 bg-white border border-[#E4D9C1] p-8 grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="contact-form">
            <div>
              <label className="luxor-label">Full Name *</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="luxor-field" data-testid="contact-name" required />
            </div>
            <div>
              <label className="luxor-label">Email *</label>
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="luxor-field" data-testid="contact-email-input" required />
            </div>
            <div>
              <label className="luxor-label">Phone</label>
              <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="luxor-field" data-testid="contact-phone-input" />
            </div>
            <div>
              <label className="luxor-label">Subject</label>
              <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="luxor-field" data-testid="contact-subject" />
            </div>
            <div className="md:col-span-2">
              <label className="luxor-label">Message *</label>
              <textarea rows={5} maxLength={800} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="luxor-field resize-none" data-testid="contact-message" required />
              <div className="text-right text-xs text-[#6B665E] mt-1">{form.message.length}/800</div>
            </div>
            <div className="md:col-span-2">
              <button type="submit" disabled={sending} className="luxor-btn-primary w-full justify-center disabled:opacity-50" data-testid="contact-submit">
                {sending ? "Sending…" : (<>Send Message <Send size={14} /></>)}
              </button>
            </div>
          </form>
        )}
      </section>
    </div>
  );
}
